import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';

export const fetchKeywordCategories = createAsyncThunk(
  'keywordCategory/fetchAll',
  async ({ page = 1, limit = 10, search, sortBy = 'createdAt', sortOrder = 'desc' } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      const res = await api.keywordCategories.getAll(params);
      if (!res.data.status) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch keyword categories');
    }
  }
);

export const fetchKeywordCategoryById = createAsyncThunk(
  'keywordCategory/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.keywordCategories.getById(id);
      if (!res.data.status) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch');
    }
  }
);

export const createKeywordCategory = createAsyncThunk(
  'keywordCategory/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.keywordCategories.create(data);
      if (!res.data.status) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to create');
    }
  }
);

export const updateKeywordCategory = createAsyncThunk(
  'keywordCategory/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.keywordCategories.update(id, data);
      if (!res.data.status) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to update');
    }
  }
);

export const deleteKeywordCategory = createAsyncThunk(
  'keywordCategory/delete',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.keywordCategories.delete(id);
      if (!res.data.status) return rejectWithValue(res.data.message);
      return id;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to delete');
    }
  }
);

export const bulkDeleteKeywordCategories = createAsyncThunk(
  'keywordCategory/bulkDeleteKeywordCategories',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.keywordCategories.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting keyword categories');
    }
  }
);

export const bulkUpdateKeywordCategoryStatus = createAsyncThunk(
  'keywordCategory/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.keywordCategories.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating keyword category status');
    }
  }
);

export const duplicateKeywordCategory = createAsyncThunk(
  'keywordCategory/duplicate',
  async (id, { rejectWithValue }) => {
    try {
      const res = await api.keywordCategories.duplicate(id);
      if (!res.data.status) return rejectWithValue(res.data.message);
      return res.data.data;
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Failed to duplicate');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  loading: false,
  error: null,
};

const keywordCategorySlice = createSlice({
  name: 'keywordCategory',
  initialState,
  reducers: {
    setSorting: (state, action) => {
      const { sortBy, sortOrder } = action.payload;
      state.sortBy = sortBy;
      state.sortOrder = sortOrder;
    },
    resetSorting: (state) => {
      state.sortBy = 'createdAt';
      state.sortOrder = 'desc';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchKeywordCategories.pending, (state) => {
        state.loading = true; state.error = null;
      })
      .addCase(fetchKeywordCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.keywordCategories || [];
        state.total = parseInt(action.payload.total || 0);
        state.page = parseInt(action.payload.page || 1);
        state.pages = parseInt(action.payload.pages || 0);
        state.limit = parseInt(action.payload.limit || 10);
        // Store current sorting parameters from the request
        const meta = action.meta.arg || {};
        state.sortBy = meta.sortBy || 'createdAt';
        state.sortOrder = meta.sortOrder || 'desc';
      })
      .addCase(fetchKeywordCategories.rejected, (state, action) => {
        state.loading = false; state.error = action.payload;
      })
      .addCase(createKeywordCategory.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createKeywordCategory.fulfilled, (state, action) => { state.loading = false; state.items.push(action.payload); })
      .addCase(createKeywordCategory.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(updateKeywordCategory.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(updateKeywordCategory.fulfilled, (state, action) => {
        state.loading = false;
        const i = state.items.findIndex(it => it._id === action.payload._id);
        if (i !== -1) state.items[i] = action.payload;
      })
      .addCase(updateKeywordCategory.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(deleteKeywordCategory.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(deleteKeywordCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(it => it._id !== action.payload);
      })
      .addCase(deleteKeywordCategory.rejected, (state) => { state.loading = false; })
      .addCase(bulkDeleteKeywordCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteKeywordCategories.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteKeywordCategories.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateKeywordCategoryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateKeywordCategoryStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateKeywordCategoryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(duplicateKeywordCategory.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(duplicateKeywordCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(duplicateKeywordCategory.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchKeywordCategoryById.fulfilled, (state, action) => {
        const i = state.items.findIndex(it => it._id === action.payload._id);
        if (i === -1) state.items.push(action.payload);
      });
  }
});

export const { setSorting, resetSorting } = keywordCategorySlice.actions;
export default keywordCategorySlice.reducer;


